import Velocity from 'velocity-animate';

import Helpers from '../helpers/helpers';

import './tabs.scss';

export enum TabsAnimationType {
    slide,
    fade,
    fadeAndSlide,
}

export enum TabsAnimationDirection {
    in,
    out,
}

export interface ITabsSettings {
    becomingOpenClass?: string;
    openClass?: string;
    currentClass?: string;
    tabsClass?: string;
    controlClass?: string;
    contentClass?: string;
    navClass?: string;
    animationSlideTime?: number;
    animationFadeTime?: number;
    animationSlideEase?: string;
    animationFadeEase?: string;
    animationType?: TabsAnimationType;
    leftClass?: string;
    rightClass?: string;
}

export const tabsSettings: ITabsSettings = {
    animationFadeEase: 'swing',
    animationFadeTime: 300,
    animationSlideEase: 'swing',
    animationSlideTime: 300,
    animationType: TabsAnimationType.fade,
    becomingOpenClass: 'is-becoming-open',
    contentClass: 'js-tabs-content',
    controlClass: 'js-tabs-control',
    currentClass: 'is-current',
    navClass: 'js-tabs-nav',
    openClass: 'is-open',
    tabsClass: 'js-tabs',
    leftClass: 'is-left',
    rightClass: 'is-right',
};

export interface IAnimationArgs {
    height: string;
    opacity: number;
}

export default class Tabs {

    settings: ITabsSettings;
    element: JQuery;
    timeout: number;

    private onOpenComplete: () => void;
    private onCloseComplete: () => void;

    constructor(target: HTMLElement, settings: ITabsSettings = { animationType: TabsAnimationType[$(target).closest('.tabs').data('animation-type') as keyof typeof TabsAnimationType] }) {
        this.settings = jQuery.extend(tabsSettings, settings) as ITabsSettings;
        this.element = $(target);
    }

    getTabs(): JQuery {
        let tabs: JQuery;

        if (this.element.hasClass(this.settings.tabsClass)) {
            tabs = this.element.addClass(this.settings.tabsClass);
        } else {
            tabs = this.element.parents('.' + this.settings.tabsClass);
        }

        return tabs.length ? $(tabs.get(0)) : tabs;
    }

    getCurrentTab(): JQuery {
        let element: JQuery;

        if (this.element.hasClass(this.settings.controlClass)) {
            element = $(this.element.attr('href'));
        } else if (this.element.hasClass(this.settings.contentClass)) {
            element = this.element;
        }

        return element;
    }

    open(tab: JQuery = this.getCurrentTab(), recursive: boolean = false, initialLoad: boolean = false): void {
        const parents: JQuery = this.getTabs();
        const parentLink: JQuery = parents.find('[href="#' + tab.attr('id') + '"].' + this.settings.controlClass);
        const hasActiveControlClass: boolean = false;

        if (parents.length) {
            parentLink.hasClass(this.settings.currentClass);
        }
        if (tab.length && !tab.hasClass(this.settings.openClass) && !hasActiveControlClass) {
            tab.addClass(this.settings.becomingOpenClass);
            this.onCloseComplete = (): void => {
                if (tab.length) {
                    tab.addClass(this.settings.openClass);
                    tab.removeClass(this.settings.becomingOpenClass);

                    if (tab.find('.image-zoom').length > 0) {
                        $(document).trigger('enhance.zoom');
                    }

                    if (this.timeout) {
                        window.clearTimeout(this.timeout);
                    }

                    // Add small timout to fix scrolling to content:
                    this.timeout = window.setTimeout(() => {
                        $(document).trigger('tab.changed');
                        if (!recursive && !Helpers.isOnScreen(tab)) {
                            Helpers.scrollToTarget(tab);
                        }
                    }, 50);
                }
                if (!recursive && !initialLoad) {
                    this.animate(TabsAnimationDirection.in, tab);
                }
            };
            if (parents.length) {
                this.closeAll(parents, recursive || initialLoad, tab);

                if (!recursive && !initialLoad) {
                    // sweet dreams
                } else {
                    this.onCloseComplete();
                }
            } else {
                this.onCloseComplete();
            }
            if (parents.length) {
                const list: JQuery = parentLink.parents().closest('.tabs__nav-list');

                parentLink.addClass(this.settings.currentClass);

                Velocity(list, 'scroll', {
                    axis: 'x',
                    container: list,
                    duration: 300,
                    offset: parentLink.offset().left - document.body.clientWidth / 2 + parentLink.width() / 2,
                });
            }
        }
    }

    close(tab: JQuery = this.getCurrentTab(), recursive: boolean = false, newTab: JQuery = null): void {
        if (tab.length) {
            const isLeft: boolean = tab.index() < newTab.index();

            tab.removeClass(this.settings.leftClass);
            tab.removeClass(this.settings.rightClass);
            tab.addClass((isLeft ? this.settings.leftClass : this.settings.rightClass));
            if (tab.hasClass(this.settings.openClass)) {
                newTab.addClass((isLeft ? this.settings.rightClass : this.settings.leftClass));
            }
            if (!recursive) {
                this.animate(TabsAnimationDirection.out, tab, (): void => {
                    tab.removeClass(this.settings.openClass);
                }, newTab);
            } else {
                tab.removeClass(this.settings.openClass);
            }
        }
    }

    closeAll(tabs: JQuery = this.getTabs(), recursive: boolean = false, newTab: JQuery = null): void {
        if (tabs.length) {
            const tab: JQuery = this.getCurrentTab();
            const open: JQuery = tab.parent().children('.' + this.settings.contentClass);

            open.each((index: number, element: HTMLElement): void => {
                if (!$(element).is(tab)) {
                    this.close($(element), recursive, newTab);
                }
            });
            tab.parent().parent().children('.' + this.settings.navClass).find('.' + this.settings.controlClass).removeClass(this.settings.currentClass);
        }
    }

    animate(type: TabsAnimationDirection, tab: JQuery = this.getCurrentTab(), onComplete: () => void = null, newtab: JQuery = null): void {
        if (tab.length) {
            const triggerOnComplete: () => void = (): void => {
                tab.removeAttr('style');
                tab.removeClass('is-animating-out');
                tab.removeClass('is-animating-in');

                if (onComplete) {
                    onComplete();
                }
                switch (type) {
                    case TabsAnimationDirection.in:
                        if (this.onOpenComplete) {
                            this.onOpenComplete();
                        }
                        this.onOpenComplete = null;
                        break;
                    case TabsAnimationDirection.out:
                        if (this.onCloseComplete) {
                            this.onCloseComplete();
                        }
                        this.onCloseComplete = null;
                        break;
                }
            };

            if (tab.length) {
                if (tab.get(0).offsetHeight === 0) {
                    tab.removeAttr('style');
                    tab.removeClass('is-animating-out');
                    tab.removeClass('is-animating-in');
                }
                const contentHeight: string = tab.get(0).offsetHeight + 'px';
                let newContentHeight: string = '0px';

                if (newtab) {
                    const isOpen: boolean = newtab.hasClass(this.settings.openClass);

                    if (!isOpen) {
                        newtab.addClass(this.settings.openClass);
                    }
                    newContentHeight = newtab.get(0).offsetHeight + 'px';
                    if (!isOpen) {
                        newtab.removeClass(this.settings.openClass);
                    }
                }
                const firstTab: JQuery = tab.length ? $(tab.get(0)) : tab;

                switch (this.settings.animationType) {
                    case TabsAnimationType.slide:
                        switch (type) {
                            case TabsAnimationDirection.in:
                                firstTab.stop().css({
                                    height: 0,
                                }).animate({
                                    height: contentHeight,
                                }, this.settings.animationFadeTime, this.settings.animationFadeEase, triggerOnComplete);
                                break;
                            case TabsAnimationDirection.out:
                                firstTab.stop().animate({
                                    height: 0,
                                }, this.settings.animationSlideTime, this.settings.animationSlideEase, triggerOnComplete);
                                break;
                        }
                        break;
                    case TabsAnimationType.fade:
                        switch (type) {
                            case TabsAnimationDirection.in:
                                firstTab.stop().addClass('is-animating-in').css({
                                    opacity: 0,
                                }).animate({
                                    opacity: 1,
                                }, this.settings.animationFadeTime, this.settings.animationFadeEase, triggerOnComplete);
                                break;
                            case TabsAnimationDirection.out:
                                firstTab.stop().addClass('is-animating-out').animate({
                                    opacity: 0,
                                }, this.settings.animationSlideTime, this.settings.animationSlideEase, triggerOnComplete);
                                break;
                        }
                        break;
                    case TabsAnimationType.fadeAndSlide:
                        switch (type) {
                            case TabsAnimationDirection.in:
                                firstTab.stop().css({
                                    height: contentHeight,
                                    opacity: 0,
                                }).animate({
                                    height: contentHeight,
                                    opacity: 1,
                                }, this.settings.animationFadeTime, this.settings.animationFadeEase, triggerOnComplete);
                                break;
                            case TabsAnimationDirection.out:
                                firstTab.stop().animate({
                                    height: newContentHeight,
                                    opacity: 0,
                                }, this.settings.animationSlideTime, this.settings.animationSlideEase, triggerOnComplete);
                                break;
                        }
                        break;
                }
            }
        }
    }
}

function onControlClick(event: JQuery.Event): void {
    event.preventDefault();
    const tabs: Tabs = new Tabs(this);

    tabs.open();
}

const onLoadHashCheck: () => void = (): void => {
    if (window.location.hash && $(window.location.hash).length && $(window.location.hash).hasClass(tabsSettings.contentClass)) {
        const tabs: Tabs = new Tabs($(window.location.hash).get(0));

        tabs.open(null, false, true);
    }
};

$((): void => {
    $(document).on('click', '.' + tabsSettings.controlClass, onControlClick);
    onLoadHashCheck();
});
